import { animate, style, transition, trigger } from '@angular/animations';

import { Component, computed, effect, EventEmitter, inject, Injector, OnDestroy, OnInit, Output, runInInjectionContext, signal, WritableSignal } from '@angular/core';
import { getDownloadURL, ref, Storage } from '@angular/fire/storage';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterModule, Scroll } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { mainRouteMenuItems } from 'src/app/app-routing.module';
import { AuthService } from 'src/app/auth.service';
import { StorageService } from 'src/app/storage.service';
import { KnowledgeNavigatorComponent } from 'src/app/views/displayer/courses/knowledge-navigator/knowledge-navigator.component';
import { NotesListComponent } from 'src/app/views/displayer/notes/notes-list/notes-list.component';
import { mySpaceRouteMenuItems } from 'src/app/views/my-space/my-space.routing';
import { MySpaceService } from 'src/app/views/my-space/my-space.service';
import { FloatEditMenuComponent } from '../float-edit-menu/float-edit-menu.component';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { CustomContextMenuService } from '../ui/custom-context-menu/custom-context-menu.service';

import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ContentSearcherComponent, ContentSearchResult } from '../../../views/content/content-searcher/content-searcher.component';

import { environment } from 'src/environments/environment';
import { AddSectionComponent } from '../../services/add-section/add-section.component';
import { TenantBrandingService } from '../../services/tenant-branding/tenant-branding.service';
import { TenantService } from '../../services/tenant/tenant.service';
import { LectureDialogComponent } from '../lecture-dialog/lecture-dialog.component';
import { ProfileDialogComponent } from '../profile-dialog/profile-dialog.component';
import { SectionDialogComponent } from '../section-dialog/section-dialog.component';
import { CustomContextMenuItem } from '../ui/custom-context-menu/custom-context-menu.component';
import { MiniCreditCounterComponent } from '../ui/mini-credit-counter/mini-credit-counter.component';
import { SelectComponent, SelectOption } from '../ui/select/select.component';
import { SplitButtonComponent } from '../ui/split-button/split-button.component';
import { SplitButtonMenuItem } from '../ui/split-button/split-button.dto';
import { UserComponent } from '../ui/user/user.component';
import { MenuItem } from './sidebar-nav.dto';

@Component({
  selector: 'app-sidebar-nav',
  imports: [
    RouterModule,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule,
    FormsModule,
    LanguageSelectorComponent,
    KnowledgeNavigatorComponent,
    FloatEditMenuComponent,
    NotesListComponent,
    UserComponent,
    SelectComponent,
    SplitButtonComponent,
    MiniCreditCounterComponent,
  ],
  templateUrl: './sidebar-nav.component.html',
  styleUrl: './sidebar-nav.component.scss',
  animations: [trigger('menuSwitch', [transition(':enter', [style({ opacity: 0 }), animate('800ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 1 }))])])],
})
export class SidebarNavComponent implements OnInit, OnDestroy {
  private routerSubscription!: Subscription;
  private userDataSubscription!: Subscription;
  @Output() closeSidebar = new EventEmitter<void>();

  public isArticleMenuOpen: WritableSignal<boolean> = signal<boolean>(false);
  public isKnowledgeMenuOpen: WritableSignal<boolean> = signal<boolean>(false);
  public isNoteMenuOpen: WritableSignal<boolean> = signal<boolean>(false);
  public isSubMenuOpen = computed(() => this.isKnowledgeMenuOpen() || this.isNoteMenuOpen());
  public pathname: WritableSignal<string> = signal<string>('');
  public currentUser = signal<any>(null);

  knowledgeId: string | null = null;
  knowledgeIdSignal: WritableSignal<string | null> = signal<string | null>(null);
  knowledgeName: string = '';
  mySpaceMenuItems: MenuItem[] = [];
  mainMenuItems: MenuItem[] = [];

  public coursePermissionsMap = signal<any>({});
  public hasWriteAccess = computed(() => {
    const id = this.knowledgeIdSignal();
    const map = this.coursePermissionsMap();
    if (!id || !map || !map[id]) return false; // Default to false if no permissions or not in a course
    const role = map[id].role;
    return ['Admin', 'Writer'].includes(role);
  });

  private router = inject(Router);
  private authService = inject(AuthService);
  private mySpaceService = inject(MySpaceService);
  private customContextMenuService = inject(CustomContextMenuService);
  private storage = inject(Storage);
  private localDbService = inject(StorageService);
  private dialog = inject(MatDialog);
  public tenantService = inject(TenantService);
  public tenantBrandingService = inject(TenantBrandingService);
  private cdkOverlay = inject(Overlay);
  private injector = inject(Injector);
  private searcherOverlayRef: OverlayRef | null = null;

  public tenantOptions = computed<SelectOption[]>(() => {
    const user = this.currentUser();
    if (!user || !user.tenants) return [];
    return user.tenants.map((t: any) => ({
      value: t.tenantId,
      label: t.target?.name || t.tenantId,
    }));
  });

  splitButtonMenuItems: SplitButtonMenuItem[] = [];
  mainSplitButtonAction: SplitButtonMenuItem;

  constructor() {
    effect(() => {
      const pathname = this.pathname();
      if (!pathname.startsWith('/knowledge/displayer/')) {
        const appName = this.tenantBrandingService.appName() || 'SkillzTank';
        document.title = appName;
      }
    });
  }

  onLogoError(event: Event): void {
    (event.target as HTMLImageElement).src = 'assets/creatives/logo-w70.png';
  }

  async ngOnInit(): Promise<void> {
    this.isKnowledgeMenuOpen.set(false);
    this.isArticleMenuOpen.set(false);
    this.mainSplitButtonAction = {
      id: 'add-lecture',
      text: 'Courses.Overview.AddLecture',
      icon: 'note_add',
      action: () => this.openLectureDialog(),
    };
    this.splitButtonMenuItems = [
      { id: 'add-section', text: 'Courses.Overview.AddSection', icon: 'create_new_folder', action: () => this.openSectionDialog() },
      { id: 'add-lecture', text: 'Courses.Overview.AddLecture', icon: 'note_add', action: () => this.openLectureDialog() },
    ];

    this.mySpaceService.isPlanFree().then((isPlanFree: boolean) => {
      if (!isPlanFree) {
        this.mainMenuItems = mainRouteMenuItems;
        this.mySpaceMenuItems = mySpaceRouteMenuItems;
      }
      this.filterMenuItems();
    });

    this.mySpaceService.coursePermissions$.subscribe((map) => {
      this.coursePermissionsMap.set(map || {});
    });

    this.pathname.set(this.router.url);
    this.loadCurrentUser();
    this.filterMenuItems();

    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd || event instanceof Scroll))
      .subscribe((event: NavigationEnd | Scroll | { type: string }) => {
        if (event instanceof Scroll) {
          const _event = event as any;
          this.pathname.set(_event.routerEvent.urlAfterRedirects || _event.routerEvent.url);
        } else if (event instanceof NavigationEnd) {
          this.pathname.set(event.urlAfterRedirects || event.url);
        } else {
          this.pathname.set(this.router.url);
        }

        let isViewingSpecificArticle = false;
        let isViewingNote = false;
        let isViewingKnowledge = false;
        const pathSegments = this.pathname().split('/');
        if (pathSegments.length === 3 && pathSegments[1] === 'articles' && pathSegments[2] && pathSegments[2] !== 'list') {
          isViewingSpecificArticle = true;
        } else if (pathSegments.length > 2 && pathSegments[1] === 'knowledge' && pathSegments[2] && pathSegments[2] !== 'list') {
          isViewingKnowledge = true;
        } else if (pathSegments.length >= 2 && pathSegments[1] === 'notes') {
          isViewingNote = true;
        }

        if (pathSegments[1] === 'knowledge' && ['overview', 'displayer'].includes(pathSegments[2])) {
          this.knowledgeId = pathSegments[3];
          this.knowledgeIdSignal.set(pathSegments[3]);
        }

        if (isViewingSpecificArticle) {
          this.isArticleMenuOpen.set(true);
          this.isKnowledgeMenuOpen.set(false);
          this.isNoteMenuOpen.set(false);
        } else if (isViewingKnowledge) {
          this.isKnowledgeMenuOpen.set(true);
          this.isArticleMenuOpen.set(false);
          this.isNoteMenuOpen.set(false);
        } else if (isViewingNote) {
          this.isNoteMenuOpen.set(true);
          this.isArticleMenuOpen.set(false);
          this.isKnowledgeMenuOpen.set(false);
        } else {
          this.isArticleMenuOpen.set(false);
          this.isKnowledgeMenuOpen.set(false);
          this.isNoteMenuOpen.set(false);
        }

        // Trigger resize so mat-sidenav recalculates drawer width
        window.dispatchEvent(new Event('resize'));
      });
  }

  openDialog(type: string, courseID: string | null = null) {
    this.dialog
      .open(AddSectionComponent, {
        data: { type, courseID },
        width: '500px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') {
          this.mySpaceService.passBlogListEvent('new-blog');
        }
      });
  }

  onSplitMenuItemClick(itemId: string): void {
    const actions = {
      'add-lecture': () => this.openLectureDialog(),
      'add-section': () => this.openSectionDialog(),
    };
    if (actions[itemId]) {
      actions[itemId]();
    }
  }

  openSectionDialog(): void {
    this.dialog
      .open(SectionDialogComponent, {
        data: { mode: 'create', courseID: this.knowledgeId, courseName: this.knowledgeName },
        minWidth: '700px',
        autoFocus: false,
        disableClose: true,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') {
          this.mySpaceService.passBlogListEvent('new-blog');
        }
      });
  }

  openLectureDialog(): void {
    this.dialog
      .open(LectureDialogComponent, {
        data: { mode: 'create', courseID: this.knowledgeId, sectionID: '' },
        width: '500px',
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res === 'ok') {
          this.mySpaceService.passBlogListEvent('new-blog');
        }
      });
  }

  loadCurrentUser(): void {
    this.userDataSubscription = this.authService.userInfo$.subscribe(async (user) => {
      if (user && Object.keys(user).length > 0) {
        const userObj = { ...user };
        userObj.photo = userObj.photo ? await this.getDownloadURL(userObj.photo) : 'assets/images/no_user.png';
        this.currentUser.set(userObj);
        // Ensure menu is recalculated when user data (like role) changes
        this.filterMenuItems();
      }
    });
  }

  async getDownloadURL(url: string): Promise<string> {
    try {
      return await runInInjectionContext(this.injector, async () => {
        const reference = ref(this.storage, url);
        return await getDownloadURL(reference);
      });
    } catch (error) {
      return 'assets/images/no_user.png';
    }
  }

  openUserMenu(event: MouseEvent): void {
    const menuItems: CustomContextMenuItem[] = [
      {
        id: 'profile',
        text: 'MySpace.Profile',
        icon: 'person',
      },
      {
        id: 'logout',
        text: 'Home.Header.logOut',
        icon: 'logout',
      },
    ];

    const trigger = event.currentTarget as HTMLElement;
    this.customContextMenuService.open(trigger, menuItems).subscribe((selection) => {
      if (selection?.actionId === 'logout') {
        this.performAction('logout');
      } else if (selection?.actionId === 'profile') {
        this.performAction('profile');
      }
    });
  }

  performAction(action?: string): void {
    if (action === 'logout') {
      this.authService.logOut();
    } else if (action === 'profile') {
      this.dialog
        .open(ProfileDialogComponent, { width: '450px', panelClass: 'standard-dialog-container' })
        .afterClosed()
        .subscribe((res) => {
          if (res) {
            this.loadCurrentUser();
          }
        });
    }
  }

  openQuickSearch(event: Event): void {
    if (this.searcherOverlayRef) {
      this.searcherOverlayRef.dispose();
      this.searcherOverlayRef = null;
      return;
    }

    const positionStrategy = this.cdkOverlay.position().global().centerHorizontally().top('10vh');

    this.searcherOverlayRef = this.cdkOverlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
    });

    const portal = new ComponentPortal(ContentSearcherComponent);
    const compRef = this.searcherOverlayRef.attach(portal);

    // Set navigation mode to replace the add icon with a navigation arrow
    compRef.setInput('mode', 'navigate');

    compRef.instance.itemSelected.subscribe((result: ContentSearchResult) => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;

      if (!result?.id) return;

      // Navigate to the correct destination based on resource type
      if (result.type === 'lecture' || result.type === 'knowledge' || result.type === 'note' || result.type === 'blog') {
        // Determine URL scheme - defaults to knowledge displayer, note displayer, or article displayer
        if (result.type === 'note') {
          this.router.navigate(['/notes/displayer', result.id]);
        } else if (result.type === 'blog') {
          this.router.navigate(['/articles/displayer', result.id]);
        } else {
          // Lecture/knowledge usually needs courseID, but if it has it in the result, or we navigate directly
          this.router.navigate(['/knowledge/displayer', 'some-course-id', 'lecture', result.id]); // Adjusting route later if needed by user
        }
      }
    });

    compRef.instance.closed.subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });

    this.searcherOverlayRef.backdropClick().subscribe(() => {
      this.searcherOverlayRef?.dispose();
      this.searcherOverlayRef = null;
    });
  }

  async switchWorkspace(tenantId: string): Promise<void> {
    await Promise.all([
      this.localDbService.clear('courses'),
      this.localDbService.clear('courseList'),
      this.localDbService.clear('blogs'),
      this.localDbService.clear('blogList'),
      this.localDbService.clear('notes'),
      this.localDbService.clear('notesList'),
    ]);

    const user = this.currentUser();
    const targetTenant = user?.tenants?.find((t: any) => t.tenantId === tenantId);

    if (targetTenant) {
      const customDomain = targetTenant.customDomain;
      const workspaceSlug = targetTenant.workspaceSlug;

      const suffix = environment.domain;
      const port = window.location.port ? `:${window.location.port}` : '';
      const protocol = window.location.protocol || 'https:';

      let targetUrl = '';
      if (customDomain && customDomain.trim() !== '') {
        targetUrl = `${protocol}//${customDomain}${port}`;
      } else if (workspaceSlug && workspaceSlug.trim() !== '' && workspaceSlug !== tenantId) {
        targetUrl = `${protocol}//${workspaceSlug}.${suffix}${port}`;
      } else {
        const mainDomain = environment.mainDomain;
        targetUrl = `${protocol}//${mainDomain}${port}`;
      }

      const isCrossDomain = targetUrl !== window.location.origin;

      // Await backend sync before navigating
      await this.tenantService.switchTenant(tenantId, isCrossDomain);

      if (isCrossDomain) {
        // Pass workspace query parameter so target origin sets active_workspace immediately
        window.location.href = `${targetUrl}/?workspace=${encodeURIComponent(tenantId)}`;
        return;
      }
    } else {
      await this.tenantService.switchTenant(tenantId);
    }
  }

  navigateBackToMainMenu(): void {
    const firstMainMenuItem = this.mainMenuItems.find((item) => item.link);
    if (firstMainMenuItem && firstMainMenuItem.link) {
      this.router.navigate([firstMainMenuItem.link]);
    } else {
      this.router.navigate(['/knowledge']);
    }
  }

  onKnowledgeName(name: string) {
    const appName = this.tenantBrandingService.appName() || 'SkillzTank';
    document.title = `${name} - ${appName}`;
    this.knowledgeName = name;
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
    this.userDataSubscription?.unsubscribe();
  }

  filterMenuItems(): void {
    const isAdmin = this.authService.isAdmin();
    const isWriter = this.authService.userInfo() && this.authService.userInfo().type === 'Writer';

    this.mainMenuItems = mainRouteMenuItems.filter((item: MenuItem) => {
      if (item.premiumOnly && this.mySpaceService.isPlanFree()) {
        return false;
      }
      if (item.adminOnly && !isAdmin) {
        return false;
      }
      if (item.writerOnly && !(isAdmin || isWriter)) {
        return false;
      }
      return true;
    });

    this.mySpaceMenuItems = mySpaceRouteMenuItems.filter((item: MenuItem) => {
      if (item.premiumOnly && this.mySpaceService.isPlanFree()) {
        return false;
      }
      if (item.adminOnly && !isAdmin) {
        return false;
      }
      if (item.writerOnly && !(isAdmin || isWriter)) {
        return false;
      }
      return true;
    });
  }
}
